import type { ItemCardSize } from '@eshop/templates/system/templateTypes';
import { DEFAULT_ITEM_CARD_SIZE } from '@theme/tokens/catalogue.ts';

export type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ColumnsByBp = Partial<Record<BreakpointKey, number>>;
export type SpansByBp = Partial<Record<BreakpointKey, number>>;

export const SIZE_TO_COLUMNS: Record<ItemCardSize, ColumnsByBp> = {
  small: { xs: 1, sm: 2, md: 3, lg: 4, xl: 5, xxl: 5 },
  normal: { xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 4 },
  large: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
};

export const columnsToSpans = (cols?: ColumnsByBp): SpansByBp => {
  if (!cols) {
    return { xs: 24 };
  }
  const out: SpansByBp = {};
  const clamp = (n: number) => Math.max(1, Math.min(24, n));

  (Object.entries(cols) as Array<[BreakpointKey, number]>).forEach(([bp, count]) => {
    if (!count || count <= 0) {
      return;
    }
    const span = clamp(Math.round(24 / count));
    out[bp] = span;
  });

  if (out.xs == null) {
    out.xs = 24;
  }

  return out;
};

export const spansToColumns = (spans?: SpansByBp): ColumnsByBp => {
  if (!spans) {
    return {};
  }

  return (Object.entries(spans) as Array<[BreakpointKey, number]>).reduce<ColumnsByBp>(
    (acc, [bp, span]) => {
      if (typeof span === 'number' && span > 0) {
        acc[bp] = Math.max(1, Math.round(24 / span));
      }
      return acc;
    },
    {},
  );
};

export const resolveItemCardSize = (
  size?: ItemCardSize | null,
  legacyMaxColumns?: number | null,
): ItemCardSize => {
  if (size === 'small' || size === 'normal' || size === 'large') {
    return size;
  }

  if (typeof legacyMaxColumns === 'number' && Number.isFinite(legacyMaxColumns)) {
    if (legacyMaxColumns >= 5) {
      return 'small';
    }

    if (legacyMaxColumns === 4) {
      return 'normal';
    }

    if (legacyMaxColumns > 0) {
      return 'large';
    }
  }

  return DEFAULT_ITEM_CARD_SIZE;
};

export const sizeToColumns = (size: ItemCardSize): ColumnsByBp => SIZE_TO_COLUMNS[size];

export const sizeToSpans = (size: ItemCardSize): SpansByBp => columnsToSpans(SIZE_TO_COLUMNS[size]);
